// All user-visible copy of V2.5 "Klick-Flow kurz" in German and English.
// The German texts are the client-approved copy (character for character as
// rendered before the i18n refactor) – do not "tidy" them. `en` is typed as
// `Dict` (= the shape of `de`), so TypeScript rejects missing or extra keys.
//
// Rich text: `**bold**` segments are rendered as <strong> by <Rich> (lang.tsx).
// Placeholders: `{title}` in calc.resultDetailsAria.

export type Lang = 'de' | 'en';

export type SheetDetail = { title: string; body: string[] };

const de = {
  seo: {
    title: 'Freiwillige SVS-Vorauszahlung – SVS-Check 2026 | PSTax',
    description:
      'Freiwillige SVS-Vorauszahlung 2026: PSTax schätzt am Jahresende deine SVS-Beiträge, damit du weißt, was auf dich zukommt – und keine Überraschung bei der Nachzahlung erlebst.',
    // Canonical = the full article (the click-through is its short version).
    canonical: 'https://pstax.at/de/guides/svs-sozialversicherung/',
  },
  legal: {
    privacyHref: '/de/datenschutz/',
    privacy: 'Datenschutz',
    imprintHref: '/de/impressum/',
    imprint: 'Impressum',
  },
  // Link to the OTHER language's page.
  langSwitch: {
    label: 'EN',
    href: '/en/guides/svs-social-insurance/short/',
    hrefLang: 'en',
    aria: 'English version',
  },
  topBar: {
    start: 'Start',
    done: 'Fertig',
    motionAria: 'Animationen an/aus',
    motionOnTitle: 'Animationen: an',
    motionOffTitle: 'Animationen: aus',
    motionOn: 'Animationen an',
    motionOff: 'Animationen aus',
    faq: 'Fragen?',
    progressAria: 'Fortschritt',
    stepDone: ' (erledigt)',
    stepCurrent: ' (aktuell)',
  },
  progress: {
    warum: 'Hintergrund',
    rechner: 'Grobe SVS-Schätzung',
    ablauf: 'Nächste Schritte',
    weiterzahlen: 'Wichtiger Hinweis',
    verstanden: 'Zusammenfassung',
  },
  buttons: {
    start: 'So funktioniert’s',
    next: 'Weiter',
    confirm: 'Alles klar',
    restart: 'Nochmal von vorne ansehen',
    back: 'Zurück',
    close: 'Schließen',
  },
  who: {
    you: 'Du',
    us: 'PSTax',
  },
  intro: {
    kicker: 'SVS-Check 2026',
    title: 'Freiwillige SVS-Vorauszahlung',
    summaryTitle: 'Das Wichtigste in 20 Sekunden',
    points: [
      'PSTax hilft dir, am Jahresende deine SVS-Beiträge zu schätzen.',
      '**Warum:** Die derzeitigen Beiträge sind nur eine Schätzung. Erst mit dem Steuerbescheid weiß die SVS, wie viel du wirklich verdient hast.',
      'Mit einer freiwilligen Vorauszahlung verringerst du die spätere Nachzahlung und vermeidest Überraschungen.',
      'Eine Vorauszahlung kann auch die Steuer für das laufende Jahr senken.',
    ],
  },
  why: {
    heading: 'Wie die SVS funktioniert',
    timelineAria: 'Zeitleiste 2026 bis 2028 – Jahr antippen zum Hervorheben',
    timeline: [
      { year: '2026', label: 'Vorläufige Beiträge' },
      { year: '2027', label: 'Steuerbescheid' },
      { year: '2028', label: 'Nachzahlung' },
    ],
    sceneMinLabel: 'z. B. 4 × 482,43 €',
    steps: [
      'Die im laufenden Jahr gezahlten SVS-Beiträge sind immer nur vorläufig.',
      'Erst durch den Steuerbescheid im Folgejahr weiß die SVS, wie viel du wirklich verdient hast, und kann die endgültigen Beiträge berechnen.',
      'Ohne unser Zutun schreibt die SVS die Nachzahlung erst im dritten Jahr vor.',
    ],
    solutionTitle: 'Unsere Lösung',
    solutionText:
      'Wir helfen dir, die Nachzahlung zu schätzen und Überraschungen zu vermeiden. Manchmal macht auch eine freiwillige Vorauszahlung Sinn.',
  },
  calc: {
    kicker: 'Rechner',
    heading: 'SVS-Schätzung',
    goodToKnow: 'Wichtig zu wissen',
    goodToKnowText:
      'Das ist nur eine **grobe erste Einschätzung** für dich. Wenn deine Daten aktuell sind, können wir ab November deinen individuellen Fall berechnen und uns bei dir melden.',
    modeLabel: 'Berechnungsvariante',
    modes: {
      revenue: 'Umsatz',
      profit: 'Gewinn',
    },
    incomeLabel: {
      revenue: 'Dein voraussichtlicher Umsatz 2026',
      profit: 'Dein voraussichtlicher Gewinn 2026',
    },
    incomeTip: {
      revenue:
        'Bei Umsätzen unter 10.000 € liegst du unter der SVS-Grenze. Eine Vorauszahlung von Beiträgen ist vermutlich nicht notwendig.',
      profit:
        'Bei Gewinnen unter 6.500 € liegst du unter der SVS-Grenze. Eine Vorauszahlung von Beiträgen ist vermutlich nicht notwendig.',
    },
    tipAria: 'Hinweis',
    svsLabel: 'SVS-Beiträge für 2026 (geschätzt)',
    paidLabel: 'Bisher für 2026 bezahlte SVS-Beiträge',
    paidWarning: {
      text: 'Nur Beiträge für 2026 zählen. Nicht alles, was du heuer an die SVS überwiesen hast, zählt auch als Vorauszahlung für das Jahr 2026.',
      detail: {
        title: 'Warum ist das wichtig?',
        body: [
          'Was du heuer an die SVS gezahlt hast, kann auch Nachzahlungen aus Vorjahren enthalten. Diese Nachzahlungen wirken nicht als Vorauszahlung für das laufende Jahr.',
          'Beispiel: Du zahlst heuer 10.000 € an die SVS. Davon sind 8.000 € eine Nachzahlung für Vorjahre – dann wirken nur 2.000 € als Vorauszahlung für 2026.',
          'Die genaue Aufteilung rechnen wir im November für dich aus – mit deinen aktuellen Daten aus dem SVS-Portal.',
        ],
      } as SheetDetail,
    },
    minNote: 'entspricht den Mindestbeiträgen',
    maxNote: 'Höchstbeitrag',
    resultLabel: 'Geschätzte Nachzahlung 2026',
    sumTotal: 'SVS 2026 gesamt',
    sumPaid: 'bereits bezahlt',
    sumOpen: 'geschätzte Nachzahlung',
    svsYear: 'SVS 2026',
    resultCaption: 'Ergebnis',
    resultDetailsAria: 'Ergebnis: {title} – Details',
    results: {
      none: {
        title: 'Keine Vorauszahlung nötig',
        text: 'Nach dieser groben Schätzung zahlst du für heuer schon genug SVS-Beiträge. Wir sehen keinen dringenden Handlungsbedarf.',
      },
      small: {
        title: 'Vorauszahlung nicht dringend',
        text: 'Unter ca. 1.000 € sehen wir eine Vorauszahlung als nicht unbedingt nötig, da die Schätzung auch abweichen kann.',
      },
      lowRate: {
        title: 'Vorauszahlung nicht dringend',
        text: 'Es wird eine Nachzahlung kommen. Eine Vorauszahlung ist aber nicht dringend nötig, weil dein Steuersatz heuer nicht besonders hoch ist. Leg dir das Geld für die Nachzahlung trotzdem zur Seite.',
      },
      relevant: {
        title: 'Vorauszahlung sinnvoll',
        text: 'Es wird vermutlich zu einer Nachzahlung von SVS-Beiträgen kommen, sodass eine freiwillige Vorauszahlung Sinn machen kann. Wir melden uns ab Anfang November mit einer genaueren Schätzung.',
      },
    },
    assumptions: {
      title: 'Annahmen',
      body: [
        'Mindestbeitrag 2026: 482,43 € pro Quartal (1.929,72 € pro Jahr).',
        'Umsatz-Variante: SVS ≈ 20 % vom Umsatz. Gewinn-Variante: SVS ≈ 26,83 % vom Gewinn.',
        'Höchstens ca. 26.200 € pro Jahr (Höchstbeitragsgrundlage 2026: 97.020 €).',
        'Der Rechner beginnt bei 10.000 € Umsatz bzw. 6.500 € Gewinn. Darunter macht eine Vorauszahlung vermutlich keinen Sinn; Pflichtbeiträge fallen in der Regel erst ab ca. 6.613 € Gewinn an.',
        'Wenn du erst während des Jahres begonnen (oder aufgehört) hast, berücksichtigt der Rechner das nicht korrekt.',
        'Vereinfacht, ohne Sonderregeln für Neugründer und Hinzurechnungen. Gerundet.',
        'Das ist nur eine grobe Schätzung.',
      ],
    } as SheetDetail,
    whenTitle: 'Wann eine freiwillige Vorauszahlung Sinn macht – und wann nicht',
    whenButton: 'Wann eine Vorauszahlung Sinn macht – und wann nicht',
    when: {
      general:
        'Generell gilt: In den meisten Fällen bedeutet eine freiwillige Vorauszahlung nur eine Verschiebung. Die Höhe der SVS-Beiträge ändert sich dadurch nicht. Es können sich aber durchaus Situationen ergeben, in denen sich insgesamt ein steuerlicher Vorteil ergibt.',
      rule: '**Die wichtigste Grundregel:** SVS-Beiträge in dem Jahr zahlen, in dem du das höchste Einkommen erzielst – dann sind der Steuersatz und damit der Steuervorteil am größten.',
      yesTitle: 'Wann eine Vorauszahlung besonders sinnvoll ist',
      noTitle: 'Wann wir eher nicht zu einer Vorauszahlung raten',
      ifLead: 'Wenn …',
      yes: [
        'du dein Business beendest oder pausierst,',
        'du nächstes Jahr deutlich weniger verdienen wirst – z. B. Auslandsaufenthalt, Karenz oder ein absehbar schwächeres Jahr,',
        'du heuer gut verdienst und die Nachzahlung nicht erst in zwei Jahren spüren willst.',
      ],
      no: [
        'dein Umsatz heuer sehr niedrig ist – z. B. weil dein Business gerade neu gestartet ist,',
        'du künftig deutlich höhere Gewinne erwartest,',
        'dein Gewinn heuer ohnehin niedrig ist, weil du hohe SVS-Nachzahlungen für Vorjahre zahlst,',
        'du dir die Vorauszahlung nicht gut leisten kannst – besonders, wenn nächstes Jahr wenig oder kein Einkommen kommt.',
      ],
    },
  },
  process: {
    heading: 'Wie wir die SVS-Vorauszahlung berechnen',
    steps: [
      {
        when: 'Bis Ende Oktober',
        title: 'Buchhaltung auf aktuellen Stand bringen',
        text: 'Die SVS-Vorausberechnung basiert auf deinem voraussichtlichen Umsatz bzw. Gewinn für heuer. Dafür brauchen wir möglichst aktuelle Zahlen in deiner Buchhaltung (Google Sheet). SVS-Daten brauchen wir von dir keine – die rufen wir selbst aus dem SVS-Portal ab.',
      },
      {
        when: 'Ab Anfang November',
        title: 'Wir melden uns schrittweise bei allen Klienten',
        text: 'Sobald die Vorschreibungen für das vierte Quartal im SVS-Portal ersichtlich sind, können wir für alle Klienten eine individuelle Berechnung erstellen. Wir melden uns schrittweise bei allen – mit einem Link zur Berechnung und genauen Anweisungen für die Zahlung.',
      },
      {
        when: 'Bis Jahresende',
        title: 'Vorauszahlung an SVS tätigen',
        text: 'Wenn die Berechnung zeigt, dass eine Vorauszahlung sinnvoll ist, ist es wichtig, dass du die Zahlung an die SVS bis Jahresende tätigst. Nur Beiträge, die noch heuer bezahlt werden, wirken sich steuerlich für heuer aus. Zur Info: Durch die Zahlung entsteht auf deinem SVS-Konto zunächst ein Guthaben. Dieses wird verbraucht, sobald die SVS die endgültigen Beiträge berechnet hat.',
      },
      {
        when: 'Nach Erhalt Steuerbescheid 2026',
        title: 'SVS-Endabrechnung wird verbucht',
        text: 'Im nächsten Jahr erstellen wir deine Steuererklärung. Sobald der Einkommensteuerbescheid verfügbar ist, würden wir (nach deinem OK) bei der SVS die vorzeitige Endabrechnung beantragen. Damit wird das Guthaben auf deinem SVS-Konto verbraucht, und du musst nicht noch ein Jahr warten, um das Jahr abzuschließen.',
      },
    ],
    note: 'Wenn du bis Mitte Dezember nichts von uns gehört hast, ist laut unserer Einschätzung nichts zu tun. Wenn du der Meinung bist, dass das ein Fehler ist, melde dich einfach bei uns!',
  },
  keepPaying: {
    heading: 'Nicht aufhören, SVS zu zahlen!',
    subheading: 'Was passiert durch die Vorauszahlung?',
    points: [
      'Deine Vorauszahlung landet als Guthaben auf deinem SVS-Konto.',
      'Die SVS weiß nicht, dass dieses Guthaben für 2026 gedacht ist.',
      'Sie zieht deshalb die Vorschreibungen der Folgequartale 2027 automatisch davon ab – scheinbar ist nichts zu zahlen.',
      'Zahl trotzdem weiter, was die SVS dir vorschreibt (Betrag laut Quartalsvorschreibung) – sonst ist das Guthaben weg, wenn die Nachzahlung kommt.',
    ],
    baseInfo: {
      title: 'Vorauszahlung ist nicht gleich Hinaufsetzen',
      body: [
        'Deine Zahlung ist eine freiwillige Vorauszahlung. Deine Beitragsgrundlage wird dadurch nicht erhöht – deine Quartalsvorschreibungen bleiben vorerst gleich.',
        'Wir setzen die Beitragsgrundlage bewusst nicht hinauf: Sonst würde die SVS sofort die Differenz für alle bisherigen Quartale nachverrechnen.',
        'Erhöht wird die Beitragsgrundlage erst, wenn die SVS mit deinem Steuerbescheid endgültig abrechnet. Ab dann steigen auch deine künftigen Vorschreibungen.',
        'Bis dahin gilt: Zahl einfach den Betrag, der auf deiner Quartalsvorschreibung steht.',
      ],
    } as SheetDetail,
  },
  summary: {
    heading: 'Zusammenfassung',
    intro:
      'Die SVS-Vorauszahlung ist **freiwillig**. Die Vorberechnung ist ein Service von uns, damit du weißt, was auf dich zukommt.',
    taxTitle: 'Spart mir das Steuern?',
    taxText:
      'Manchmal ergibt sich durch die Vorauszahlung eine echte Steuerersparnis, die es sonst nicht gegeben hätte. In vielen Fällen ist sie aber sowohl bei der Steuer als auch bei der SVS nur eine Verschiebung.',
    tip: '**Unser Tipp:** Wenn du am Jahresende noch Geld für dein Business einsetzen willst, ist eine freiwillige SVS-Vorauszahlung aus unserer Sicht ein guter Anfang – die Beiträge kommen ohnehin auf dich zu.',
    laptop: {
      title: 'Alternativinvestition: Laptop',
      body: [
        'Was du für dein Business wirklich brauchst, sollst du natürlich kaufen. Nur als Steuertrick lohnen sich Käufe im Dezember oft weniger als gedacht.',
        'Beispiel: Ein Laptop um 2.000 € im Dezember wird über 4 Jahre abgeschrieben, also 500 € pro Jahr. Weil du ihn erst im zweiten Halbjahr kaufst, zählt heuer nur die Hälfte: 250 €. Bei 40 % Privatanteil wirken heuer nur rund 150 € steuerlich.',
        'Dieselben 2.000 € als freiwillige SVS-Vorauszahlung zählen heuer voll als Betriebsausgabe.',
      ],
    } as SheetDetail,
    todoTitle: 'Was du tun musst',
    todoText:
      'Wir brauchen eine grobe Schätzung deines Umsatzes bzw. Gewinns bis Jahresende. Du musst uns jetzt noch nichts schicken – wir kommen ab November auf dich zu!',
  },
  done: {
    heading: 'Danke – wir melden uns ab Anfang November.',
    close: 'Du kannst dieses Fenster jetzt schließen.',
  },
  faq: {
    title: 'Fragen?',
    items: [
      {
        q: 'Muss ich das machen?',
        a: 'Nein. Alles ist freiwillig. Wir sagen dir nur rechtzeitig, was auf dich zukommt.',
      },
      {
        q: 'Was, wenn wir zu hoch schätzen?',
        a: 'Dann bleibt ein Guthaben auf deinem SVS-Konto und wird mit späteren Beiträgen verrechnet. Es geht nicht verloren. Eine Auszahlung gilt steuerlich als Betriebseinnahme – wir raten davon ab.',
      },
      {
        q: 'Ich habe gerade gegründet – gilt das für mich?',
        a: 'In den ersten Jahren gelten bei der SVS Sonderregeln. Die berücksichtigen wir in unserer internen Berechnung – du musst nichts extra tun.',
      },
      {
        q: 'Wie genau ist die Schätzung?',
        a: 'Es handelt sich um eine grobe Vorberechnung. Aus unserer Erfahrung macht eine genaue Berechnung zu diesem Zeitpunkt keinen Sinn, da sich bei Umsatz, Ausgaben und anderen Faktoren bis Jahresende noch einiges ändern kann.',
      },
    ],
    contact: 'Fragen? Schreib uns einfach – wir sind für dich da.',
    motionLabel: 'Animationen',
    motionOn: 'an',
    motionOff: 'aus',
    footerNote:
      'Stand: September 2026. Vereinfachte, allgemeine Information für Klient:innen von PSTax – keine individuelle Beratung. Geprüft von Pascal Schraml, Steuerberater, PSTax Steuerberatung FlexCo.',
  },
};

export type Dict = typeof de;

const en: Dict = {
  seo: {
    title: 'Voluntary SVS prepayment – SVS check 2026 | PSTax',
    description:
      'Voluntary SVS prepayment 2026: PSTax estimates your SVS contributions at year-end so you know what’s coming – and the back payment doesn’t take you by surprise.',
    canonical: 'https://pstax.at/en/guides/svs-social-insurance/',
  },
  legal: {
    privacyHref: '/en/privacy-policy/',
    privacy: 'Privacy',
    imprintHref: '/en/imprint/',
    imprint: 'Imprint',
  },
  langSwitch: {
    label: 'DE',
    href: '/de/guides/svs-sozialversicherung/kurz/',
    hrefLang: 'de',
    aria: 'Deutsche Version',
  },
  topBar: {
    start: 'Start',
    done: 'Done',
    motionAria: 'Animations on/off',
    motionOnTitle: 'Animations: on',
    motionOffTitle: 'Animations: off',
    motionOn: 'Animations on',
    motionOff: 'Animations off',
    faq: 'Questions?',
    progressAria: 'Progress',
    stepDone: ' (done)',
    stepCurrent: ' (current)',
  },
  progress: {
    warum: 'Background',
    rechner: 'Rough SVS estimate',
    ablauf: 'Next steps',
    weiterzahlen: 'Important',
    verstanden: 'Summary',
  },
  buttons: {
    start: 'How it works',
    next: 'Next',
    confirm: 'Got it',
    restart: 'See it again from the start',
    back: 'Back',
    close: 'Close',
  },
  who: {
    you: 'You',
    us: 'PSTax',
  },
  intro: {
    kicker: 'SVS check 2026',
    title: 'Voluntary SVS prepayment',
    summaryTitle: 'The key points in 20 seconds',
    points: [
      'PSTax helps you estimate your SVS contributions (Austrian social insurance for the self-employed) at the end of the year.',
      '**Why:** Your current contributions are only an estimate. The SVS only knows how much you really earned once your income tax assessment (Einkommensteuerbescheid) is out.',
      'A voluntary prepayment reduces the back payment later on and helps you avoid surprises.',
      'A prepayment can also lower your tax for the current year.',
    ],
  },
  why: {
    heading: 'How the SVS works',
    timelineAria: 'Timeline 2026 to 2028 – tap a year to highlight it',
    timeline: [
      { year: '2026', label: 'Provisional contributions' },
      { year: '2027', label: 'Tax assessment' },
      { year: '2028', label: 'Back payment' },
    ],
    sceneMinLabel: 'e.g. 4 × €482.43',
    steps: [
      'The SVS contributions you pay during the year are always provisional.',
      'Only when your tax assessment arrives the following year does the SVS know how much you really earned – and can work out your final contributions.',
      'Unless we step in, the SVS only bills you for the back payment in the third year.',
    ],
    solutionTitle: 'Our solution',
    solutionText:
      'We help you estimate the back payment and avoid surprises. Sometimes a voluntary prepayment makes sense, too.',
  },
  calc: {
    kicker: 'Calculator',
    heading: 'SVS estimate',
    goodToKnow: 'Good to know',
    goodToKnowText:
      'This is just a **rough first estimate** for you. If your figures are up to date, we can calculate your individual case from November and get in touch with you.',
    modeLabel: 'Calculate with',
    modes: {
      revenue: 'Revenue',
      profit: 'Profit',
    },
    incomeLabel: {
      revenue: 'Your expected revenue 2026',
      profit: 'Your expected profit 2026',
    },
    incomeTip: {
      revenue:
        'With revenue under €10,000 you’re below the SVS threshold. A prepayment of contributions is probably not necessary.',
      profit:
        'With profit under €6,500 you’re below the SVS threshold. A prepayment of contributions is probably not necessary.',
    },
    tipAria: 'Note',
    svsLabel: 'SVS contributions for 2026 (estimated)',
    paidLabel: 'SVS contributions already paid for 2026',
    paidWarning: {
      text: 'Only contributions for 2026 count. Not everything you’ve paid to the SVS this year counts as a prepayment for 2026.',
      detail: {
        title: 'Why does this matter?',
        body: [
          'What you’ve paid to the SVS this year may include back payments for previous years. Those don’t count as a prepayment for the current year.',
          'Example: you pay €10,000 to the SVS this year. €8,000 of that is a back payment for previous years – so only €2,000 counts as a prepayment for 2026.',
          'We’ll work out the exact split for you in November – using your current data from the SVS portal.',
        ],
      },
    },
    minNote: 'equals the minimum contributions',
    maxNote: 'maximum contribution',
    resultLabel: 'Estimated back payment 2026',
    sumTotal: 'SVS 2026 total',
    sumPaid: 'already paid',
    sumOpen: 'estimated back payment',
    svsYear: 'SVS 2026',
    resultCaption: 'Result',
    resultDetailsAria: 'Result: {title} – details',
    results: {
      none: {
        title: 'No prepayment needed',
        text: 'Based on this rough estimate, you’re already paying enough SVS contributions for this year. We don’t see any urgent need to act.',
      },
      small: {
        title: 'Prepayment not urgent',
        text: 'Below approx. €1,000 we don’t consider a prepayment really necessary, as the estimate may be a little off.',
      },
      lowRate: {
        title: 'Prepayment not urgent',
        text: 'A back payment will come. But a prepayment isn’t urgent, because your tax rate this year isn’t particularly high. Still, put the money for the back payment aside.',
      },
      relevant: {
        title: 'Prepayment makes sense',
        text: 'You’ll probably have a back payment of SVS contributions, so a voluntary prepayment can make sense. We’ll get in touch from early November with a more accurate estimate.',
      },
    },
    assumptions: {
      title: 'Assumptions',
      body: [
        'Minimum contribution 2026: €482.43 per quarter (€1,929.72 per year).',
        'Revenue option: SVS ≈ 20% of revenue. Profit option: SVS ≈ 26.83% of profit.',
        'At most approx. €26,200 per year (maximum contribution base 2026: €97,020).',
        'The calculator starts at €10,000 revenue or €6,500 profit. Below that, a prepayment probably doesn’t make sense; compulsory contributions usually only apply from a profit of approx. €6,613.',
        'If you only started (or stopped) during the year, the calculator doesn’t take that into account correctly.',
        'Simplified, without the special rules for new businesses and add-backs. Rounded.',
        'This is only a rough estimate.',
      ],
    },
    whenTitle: 'When a voluntary prepayment makes sense – and when it doesn’t',
    whenButton: 'When a prepayment makes sense – and when it doesn’t',
    when: {
      general:
        'In general: in most cases a voluntary prepayment only shifts the timing. It doesn’t change the amount of your SVS contributions. But there can be situations where it gives you an overall tax advantage.',
      rule: '**The most important rule:** pay SVS contributions in the year you earn the most – that’s when your tax rate, and so your tax saving, is highest.',
      yesTitle: 'When a prepayment makes particular sense',
      noTitle: 'When we’d rather not recommend a prepayment',
      ifLead: 'If …',
      yes: [
        'you’re closing or pausing your business,',
        'you’ll earn much less next year – e.g. time abroad, parental leave or a year you already know will be weaker,',
        'you’re earning well this year and don’t want to feel the back payment only two years from now.',
      ],
      no: [
        'your revenue this year is very low – e.g. because your business has only just started,',
        'you expect much higher profits in the future,',
        'your profit this year is low anyway because you’re paying large SVS back payments for previous years,',
        'you can’t comfortably afford the prepayment – especially if little or no income is coming in next year.',
      ],
    },
  },
  process: {
    heading: 'How we calculate your SVS prepayment',
    steps: [
      {
        when: 'By end of October',
        title: 'Bring your bookkeeping up to date',
        text: 'The SVS pre-calculation is based on your expected revenue or profit for this year. For that, we need the figures in your bookkeeping (Google Sheet) to be as up to date as possible. We don’t need any SVS data from you – we get that ourselves from the SVS portal.',
      },
      {
        when: 'From early November',
        title: 'We get in touch with all clients, one by one',
        text: 'As soon as the quarterly SVS bill (Vorschreibung) for the fourth quarter is visible in the SVS portal, we can prepare an individual calculation for every client. We’ll contact everyone step by step – with a link to your calculation and clear instructions for the payment.',
      },
      {
        when: 'By the end of the year',
        title: 'Make the prepayment to the SVS',
        text: 'If the calculation shows that a prepayment makes sense, it’s important that you pay the SVS by the end of the year. Only contributions paid this year count for this year’s tax. Good to know: the payment first shows up as a credit on your SVS account. It’s used up once the SVS has calculated your final contributions.',
      },
      {
        when: 'After your 2026 tax assessment',
        title: 'SVS final settlement is booked',
        text: 'Next year we’ll prepare your tax return. As soon as your income tax assessment is available, we’d (with your OK) apply to the SVS for an early final settlement. That uses up the credit on your SVS account, and you don’t have to wait another year to close the year.',
      },
    ],
    note: 'If you haven’t heard from us by mid-December, there’s nothing to do as far as we can tell. If you think that’s a mistake, just get in touch!',
  },
  keepPaying: {
    heading: 'Don’t stop paying the SVS!',
    subheading: 'What happens when you prepay?',
    points: [
      'Your prepayment sits as a credit on your SVS account.',
      'The SVS doesn’t know that this credit is meant for 2026.',
      'So it automatically takes your quarterly bills for 2027 out of it – it looks as if there’s nothing to pay.',
      'Keep paying what the SVS bills you anyway (the amount on your quarterly bill) – otherwise the credit is gone by the time the back payment comes.',
    ],
    baseInfo: {
      title: 'Prepaying is not the same as raising your contribution base',
      body: [
        'Your payment is a voluntary prepayment. It doesn’t raise your contribution base – your quarterly bills stay the same for now.',
        'We deliberately don’t raise the contribution base: otherwise the SVS would immediately charge the difference for all previous quarters.',
        'Your contribution base is only raised once the SVS does the final settlement based on your tax assessment. From then on, your future quarterly bills go up too.',
        'Until then: just pay the amount on your quarterly bill.',
      ],
    },
  },
  summary: {
    heading: 'Summary',
    intro:
      'The SVS prepayment is **voluntary**. The pre-calculation is a service from us, so you know what’s coming.',
    taxTitle: 'Does this save me tax?',
    taxText:
      'Sometimes a prepayment creates a real tax saving that you wouldn’t have had otherwise. But in many cases it just shifts the timing – for both tax and SVS.',
    tip: '**Our tip:** If you still want to put money into your business at the end of the year, we think a voluntary SVS prepayment is a good place to start – you’ll have to pay those contributions anyway.',
    laptop: {
      title: 'Alternative investment: a laptop',
      body: [
        'Of course you should buy what your business really needs. But as a pure tax trick, December purchases often pay off less than you’d think.',
        'Example: a €2,000 laptop bought in December is depreciated over 4 years, so €500 a year. Because you buy it in the second half of the year, only half counts this year: €250. With 40% private use, only around €150 counts for tax this year.',
        'The same €2,000 as a voluntary SVS prepayment counts in full as a business expense this year.',
      ],
    },
    todoTitle: 'What you need to do',
    todoText:
      'We need a rough estimate of your revenue or profit up to the end of the year. You don’t need to send us anything yet – we’ll get in touch from November!',
  },
  done: {
    heading: 'Thanks – we’ll be in touch from early November.',
    close: 'You can close this window now.',
  },
  faq: {
    title: 'Questions?',
    items: [
      {
        q: 'Do I have to do this?',
        a: 'No. It’s all voluntary. We just let you know in good time what’s coming.',
      },
      {
        q: 'What if we estimate too high?',
        a: 'Then the credit stays on your SVS account and is set off against later contributions. It isn’t lost. A refund counts as business income for tax purposes – we advise against it.',
      },
      {
        q: 'I’ve only just started my business – does this apply to me?',
        a: 'The SVS has special rules for the first few years. We take them into account in our internal calculation – you don’t need to do anything extra.',
      },
      {
        q: 'How accurate is the estimate?',
        a: 'It’s a rough pre-calculation. In our experience, an exact calculation doesn’t make sense at this point, as a lot can still change in revenue, expenses and other factors before the end of the year.',
      },
    ],
    contact: 'Questions? Just write to us – we’re here for you.',
    motionLabel: 'Animations',
    motionOn: 'on',
    motionOff: 'off',
    footerNote:
      'As of September 2026. Simplified, general information for PSTax clients – not individual advice. Reviewed by Pascal Schraml, tax advisor, PSTax Steuerberatung FlexCo.',
  },
};

export const dict: Record<Lang, Dict> = { de, en };

/** Whole euros: German "€ 50.000" (de-AT), English "€50,000" (en-IE). */
export function formatEuro(value: number, lang: Lang) {
  return new Intl.NumberFormat(lang === 'en' ? 'en-IE' : 'de-AT', {
    style: 'currency',
    currency: 'EUR',
    // Whole euros, except exact amounts like the minimum contribution (1.929,72 €).
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
}
